import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { masterChartPath } from 'constants/masterChartRoutes';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Paper,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { ArrowBackRounded } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { differenceInYears } from 'date-fns';
import { AuthContext } from 'context/Auth';
import { db } from 'services/firebase';
import { contentCardSx } from 'styles/standaloneLayoutTokens';
import DashboardPageHeader from 'components/common/DashboardPageHeader';
import ProvenanceInfoButton from 'components/individual/health-records/ProvenanceInfoButton';

const DemographicsPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [fullHealthRecords, setFullHealthRecords] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const { control, getValues, reset, watch } = useForm({
    defaultValues: {
      dateOfBirth: null,
      sex: '',
      weight: '',
    },
  });

  const watchedDob = watch('dateOfBirth');
  const age =
    watchedDob instanceof Date && !Number.isNaN(watchedDob.getTime())
      ? differenceInYears(new Date(), watchedDob)
      : null;

  useEffect(() => {
    if (!user?.uid) return;
    const load = async () => {
      setLoading(true);
      try {
        const doc = await db.collection('users').doc(user.uid).get();
        const hr = doc.data()?.healthRecords || {};
        setFullHealthRecords(hr);
        reset({
          dateOfBirth: hr.dateOfBirth ? new Date(hr.dateOfBirth) : null,
          sex: hr.sex || '',
          weight: hr.weight != null ? String(hr.weight) : '',
        });
      } catch {
        setError(
          'Unable to load your demographics — retry before editing or saving (a failed load can look like blank fields).',
        );
        setFullHealthRecords(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.uid, reset]);

  const loadFailed = Boolean(error) && fullHealthRecords == null;

  const handleSave = useCallback(async () => {
    if (!user?.uid || saving || fullHealthRecords == null) return;
    setSaving(true);
    setError(null);
    try {
      const values = getValues();
      const weightNum = values.weight !== '' ? parseFloat(values.weight) : null;
      const dob =
        values.dateOfBirth instanceof Date &&
        !Number.isNaN(values.dateOfBirth.getTime())
          ? values.dateOfBirth.toISOString().split('T')[0]
          : null;

      const updates = {};
      if (dob) updates.dateOfBirth = dob;
      if (values.sex) updates.sex = values.sex;
      if (weightNum != null && !Number.isNaN(weightNum)) {
        updates.weight = weightNum;
        updates.weightDate = new Date().toISOString().split('T')[0];
      }

      const merged = {
        ...(fullHealthRecords ?? {}),
        ...updates,
        updatedAt: new Date().toISOString(),
      };
      await db
        .collection('users')
        .doc(user.uid)
        .set({ healthRecords: merged }, { merge: true });
      setFullHealthRecords(merged);
      setSuccessMsg('Saved.');
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [user?.uid, saving, getValues, fullHealthRecords]);

  if (loading) {
    return (
      <div className="inner">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className="inner">
        <Box sx={{ mt: { xs: 1, sm: 5 }, mb: 1 }}>
          <Button
            startIcon={<ArrowBackRounded />}
            onClick={() => navigate(masterChartPath())}
            size="small"
          >
            Back to Health Records
          </Button>
        </Box>
        <DashboardPageHeader
          title="Demographics"
          subtitle={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <span>Date of birth, sex, and weight.</span>
              <ProvenanceInfoButton source={fullHealthRecords?.demographicsSource} />
            </Box>
          }
          sx={{ mt: 0 }}
        />

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {successMsg && (
          <Alert
            severity="success"
            onClose={() => setSuccessMsg(null)}
            sx={{ mb: 2 }}
          >
            {successMsg}
          </Alert>
        )}

        <Paper elevation={0} sx={{ ...contentCardSx, p: 3, mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="dateOfBirth"
                control={control}
                render={({ field, fieldState: { error: fieldError } }) => (
                  <DatePicker
                    value={field.value}
                    onChange={(date) => field.onChange(date)}
                    maxDate={new Date()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        label: 'Date of Birth',
                        error: !!fieldError,
                        helperText:
                          fieldError?.message ??
                          (age != null ? `Age: ${age} years` : ''),
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="sex"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel id="sex-label">Sex</InputLabel>
                    <Select
                      {...field}
                      labelId="sex-label"
                      label="Sex"
                    >
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                      <MenuItem value="prefer-not-to-say">
                        Prefer not to say
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="weight"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Weight (lb)"
                    type="number"
                    fullWidth
                    inputProps={{ min: 0, step: 0.1 }}
                    helperText="Used for weight-based medical guidance."
                  />
                )}
              />
            </Grid>
          </Grid>
        </Paper>

        <LoadingButton
          variant="contained"
          onClick={handleSave}
          loading={saving}
          disabled={saving || loadFailed}
        >
          Save
        </LoadingButton>
      </div>
    </LocalizationProvider>
  );
};

export default DemographicsPage;
